import { debounce } from "lodash";
import { useCallback, useEffect, useRef, useState } from "react";
import update from "react-addons-update";
import SVG from "react-inlinesvg";
import { SudoButton } from "src/sudokit/components/SudoButton";
import Auth from "../../Auth";
import ProTip from "./shared/ProTip";
import { PROTIPS } from "./staticData";
import {
  StyledBlankSlate,
  StyledBrainformContainer,
  StyledErrorMessage,
  StyledExamplesContainer,
  StyledGrid,
  StyledGridItem,
  StyledH1,
  StyledModal,
  StyledReviewContainer,
  StyledStartOverProTip,
  StyledStreamingListContainer,
} from "./styles";

import { sendAppEvent } from "../../Utils";
import Brainform from "./Brainform";
import ReviewBrainstorm from "./ReviewBrainstorm";
import { EXAMPLES2 } from "./staticData";
import StreamingList from "./StreamingList";
import { createBrainstormSaver } from "./saveBrainstorm";

const Brainstorm = ({ documentId, listId, closeBrainstorm, seenTips, setSeenTips }) => {
  const [cardId, setCardId] = useState(false); // id of the card being brainstormed
  const [items, setItems] = useState([]); // the items
  const [starters, setStarters] = useState([""]); // the starters
  const [submitting, setSubmitting] = useState(false); // whether we're currently submitting to the AI
  const [prompt, setPrompt] = useState(""); // the prompt to be used
  const [context, setContext] = useState(""); // the context to be used
  const [saved, setSaved] = useState(null); // whether we've saved
  const [loading, setLoading] = useState(false); // whether we're currently loading
  const [currentScreen, setCurrentScreen] = useState(0); // the screen we're on
  const [list, setList] = useState("something-else"); // the list we're on
  const [proTip, setProTip] = useState(0); //first protip
  const [showProTip, setShowProTip] = useState(false); //whether to show protip
  const [keepers, setKeepers] = useState([]); // the keepers
  const [rejected, setRejected] = useState([]); //the rejected cards
  const [queuedItems, setQueuedItems] = useState([]); //items waiting to be added to the list
  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);
  const [queueing, setQueueing] = useState(false); //whether we're currently queueing
  const [hasThumbsedUp, setHasThumbsedUp] = useState(false); // so we can know when to save
  const [hasEditedStarters, setHasEditedStarters] = useState(false); // the other reason we will save
  const [promptPlaceholder, setPromptPlaceholder] = useState(""); // used for "loading..." messages
  const [ignoreItemCount, setIgnoreItemCount] = useState(false); // so we can load more items while holding shift
  const [animating, setAnimating] = useState(false); // whether we're currently animating
  const [startedOver, setStartedOver] = useState(false); // whether we've started over
  const [latestRemoved, setLatestRemoved] = useState(false); // the latest removed item, false or index
  const [errorMessage, setErrorMessage] = useState("");
  const [uiScale, setUiScale] = useState(1);
  const [hasNonRetryableError, setHasNonRetryableError] = useState(false); // prevent retries on 400-level errors

  const userInteracted = useRef(false);
  const wrapperRef = useRef(null);
  const formRef = useRef(null);
  const needsSave = useRef(false);
  const closeRef = useRef(async () => {});
  const unmountedRef = useRef(false);
  const cardIdRef = useRef(false);
  const createInFlightRef = useRef(null);

  useEffect(() => {
    return () => {
      unmountedRef.current = true;
    };
  }, []);

  // Focus the first interactive element when the modal opens
  useEffect(() => {
    if (wrapperRef.current) {
      const focusable = wrapperRef.current.querySelector('button, [tabindex="0"], a[href], input, textarea');
      if (focusable) focusable.focus();
    }
  }, []);

  // Trap focus within the modal
  useEffect(() => {
    const modal = wrapperRef.current;
    if (!modal) return;

    const handleKeyDown = (e) => {
      if (e.key !== "Tab") return;

      const focusableEls = modal.querySelectorAll(
        'button:not([disabled]), [tabindex="0"], a[href], input:not([disabled]), textarea:not([disabled]), select:not([disabled])'
      );
      if (focusableEls.length === 0) return;

      const first = focusableEls[0];
      const last = focusableEls[focusableEls.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  let enabled = !submitting && prompt.length > 10;

  const close = async (endTimeOverride) => {
    save.current.cancel(); // Prevent debounced save from firing after close
    if (hasThumbsedUp || hasEditedStarters) {
      await saveImmediate.current({ starters, rejected, context, keepers, prompt, startTime, endTime: endTimeOverride ?? endTime, list, needsSaveRef: needsSave, documentId });
    }
    closeBrainstorm();
  };
  closeRef.current = close;

  const saverRef = useRef(
    createBrainstormSaver({
      fetchWithIdToken: Auth.fetchWithIdToken,
      onCardCreated: (id) => {
        cardIdRef.current = id;
        setCardId(id);
        setErrorMessage("");
      },
      onSaveStart: () => setSaved(false),
      onSaveComplete: () => setSaved(true),
      onError: (msg) => setErrorMessage(msg),
      unmountedRef,
      cardIdRef,
      createInFlightRef,
    })
  );
  const saveImmediate = useRef(saverRef.current.saveImmediate);

  const save = useRef(debounce((...args) => saveImmediate.current(...args), 1000, { maxWait: 3000 }));

  useEffect(() => {
    if (errorMessage === "") return;

    const timeout = setTimeout(() => {
      setErrorMessage("");
    }, 6000);

    return () => clearTimeout(timeout);
  }, [errorMessage]);
  // useEffect(() => {
  //   const screenDims = {
  //     0: { height: 660, width: 950, scale: 0.8 },
  //     1: { height: 660, width: 950, scale: 0.75 },
  //     2: { width: 802, height: 745, scale: 0.76 },
  //     3: { width: 840, height: 745, scale: 0.8 },
  //   };

  //   const { height, width, scale } = screenDims[currentScreen];

  //   let windowScale = Math.min(
  //     window.innerWidth / width,
  //     window.innerHeight / height
  //   );

  //   windowScale = Math.max(windowScale, 1);

  //   let preScale = windowScale * scale;
  //   let scaleMin = 0.8;
  //   let scaleMax = 1.5;

  //   if (preScale < scaleMin) {
  //     preScale = scaleMin;
  //   } else if (preScale > scaleMax) {
  //     preScale = scaleMax;
  //   }

  //   setUiScale(preScale);
  // }, [window.innerHeight, window.innerWidth, currentScreen]);

  function submit(e) {
    e.preventDefault();
    userInteracted.current = true;
    setHasNonRetryableError(false);
    setSubmitting(true);
    sendAppEvent("Brainstorm - Screen 2 Start Button");
  }

  useEffect(() => {
    // ON LOAD: check if its an existing or new brainstorm
    if (listId) {
      cardIdRef.current = listId;
      setCardId(listId);
      setLoading(true);
      setPromptPlaceholder("Loading...");
      // load the list

      Auth.fetchWithIdToken("/api/brainstorm/load", {
        cardId: listId,
      })
        .then((result) => {
          if (unmountedRef.current) return;
          setPrompt(result.prompt);
          setKeepers(result.keepers);
          setList(result.exampleList || "something-else");
          setContext(result.context || "");
          setRejected(result.rejected || []);
          setStarters(result.starters || []);
          setLoading(false);
          setPromptPlaceholder("");
        })
        .catch((err) => {
          if (unmountedRef.current) return;
          console.log(err);
          setPromptPlaceholder("Error loading brainstorm");
          sendAppEvent("Brainstorm - User saw loading error", {
            cardId: listId,
          });
          setLoading(false);
        });

      sendAppEvent("Brainstorm - Loaded");
      setCurrentScreen(1);
    } else {
      sendAppEvent("Brainstorm - Started");
    }

    window.history.pushState({}, "", "");

    const handlePopState = (event) => {
      event.preventDefault();
      closeRef.current();
    };

    window.addEventListener("popstate", handlePopState);

    setStartTime(new Date().getTime());

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, []);

  function makeStartersKeepers() {
    if (starters.length > 0) {
      let newKeepers = starters.filter((starter, i) => {
        // is starter empty?

        if (starter.trim() === "") {
          return false;
        }

        //see if this starter is already in keepers
        let found = keepers.find((keeper) => {
          return starter === keeper;
        });

        return !found;
      });

      console.log("newKeepers", newKeepers);
      setKeepers([...newKeepers, ...keepers]);
    }
  }

  function makeLowerLists() {
    //make an array of all list items but lowercase
    const lists = [...starters, ...items, ...keepers, ...rejected];
    const lowerLists = lists
      .filter((item) => {
        return item && item.length && typeof item == "string";
      })
      .map((item) => {
        return item.toLowerCase();
      });

    return lowerLists;
  }

  const handleSeenTip = (tip) => {
    setSeenTips([...seenTips, tip]);
    setShowProTip(false);
  };

  const handleTerminalOutcome = useCallback((result) => {
    if (result?.outcome !== "refusal" && result?.outcome !== "empty") return false;
    setHasNonRetryableError(true);
    setErrorMessage(result.message);
    setSubmitting(false);
    setQueueing(false);
    sendAppEvent("Brainstorm - User saw AI error", { cardId: listId, outcome: result.outcome });
    return true;
  }, [listId]);

  useEffect(() => {
    let requestTimeout;
    let animateTimeout;

    // our queueing logic
    const lowerLists = makeLowerLists();
    const queuedItemsNeeded = queuedItems ? 6 - queuedItems.length : 6;
    let itemsNeeded = items ? 6 - items.length : 6;

    if (prompt?.length > 10 && queuedItemsNeeded > 0 && currentScreen === 2 && !submitting && !animating && !hasNonRetryableError) {
      setSubmitting(true);
      //gets items from the server and throws them in the queue
      let data = {};
      data.documentId = documentId;
      data.prompt = prompt;
      if (context.length) data.context = context;
      if (list.length) data.user_selected_list = list;
      if (starters.length) data.user_starters = starters;
      if (rejected.length) data.user_rejected = rejected;

      data.items = [];

      // or use keepers if we have a list of those
      if (keepers.length) {
        data.items = keepers.filter((item) => {
          return item.length > 0;
        });
      }

      data.n = queuedItemsNeeded + 1;

      // Add better error handling and timeout for API requests
      requestTimeout = setTimeout(() => {
        if (unmountedRef.current) return;
        console.error("[BRAINSTORM DEBUG] Follow-up request timed out");
        setErrorMessage("Request timed out. Please try again.");
        setSubmitting(false);
        setQueueing(false);
      }, 30000); // 30 second timeout

      Auth.fetchWithIdToken("/api/ai/brainstorm", data)
        .then((result) => {
          if (unmountedRef.current) return;
          clearTimeout(requestTimeout);

          if (handleTerminalOutcome(result)) return;

          // Validate result has expected properties
          if (!result) {
            console.error("[BRAINSTORM DEBUG] Empty result received");
            setErrorMessage("Invalid response received. Please try again.");
            setSubmitting(false);
            setQueueing(false);
            return;
          }

          if (!result.items) {
            console.error("[BRAINSTORM DEBUG] Result missing items array:", result);
            setErrorMessage("Unexpected response format. Please try again.");
            setSubmitting(false);
            setQueueing(false);
            return;
          }

          if (result.items.length > 0) {
            try {
              let newQueuedItems = update(
                queuedItems.filter((item) => {
                  return item.length > 0 && !lowerLists.includes(item.toLowerCase());
                }),
                { $push: result.items },
              );

              //remove duplicates in newQueuedItems
              newQueuedItems = [...new Set(newQueuedItems)];

              setQueuedItems(newQueuedItems);
              setSubmitting(false);
              setErrorMessage("");
            } catch (err) {
              console.error("[BRAINSTORM DEBUG] Error processing follow-up results:", err);
              setErrorMessage("Error processing results. Please try again.");
              setSubmitting(false);
              setQueueing(false);
            }
          } else {
            setQueueing(false);
            if (itemsNeeded > 0) {
              // only show this if we don't have enough items showing
              setErrorMessage("The AI is stumped. Try adding more examples or context.");
              sendAppEvent("Brainstorm - User saw AI error", {
                cardId: listId,
              });
            }
            setSubmitting(false);
          }
        })
        .catch((err) => {
          if (unmountedRef.current) return;
          clearTimeout(requestTimeout);
          console.error("[BRAINSTORM DEBUG] Follow-up request error:", err);

          // Check if it's a 400-level error (client error) - don't retry these
          if (err.status >= 400 && err.status < 500) {
            setHasNonRetryableError(true);
            setErrorMessage("There was an issue with your request. Try changing your prompt or starting over.");
            sendAppEvent("Brainstorm - User saw client error", {
              cardId: listId,
              error: err.message || "Unknown error",
              status: err.status,
            });
          } else {
            setErrorMessage("Connection error. Please try again in a few seconds.");
            sendAppEvent("Brainstorm - User saw connection error", {
              cardId: listId,
              error: err.message || "Unknown error",
            });
          }

          setQueueing(false);
          setSubmitting(false);
        });
    }

    if (ignoreItemCount) itemsNeeded = 5;
    // for moving items from the queue to the items list
    if (itemsNeeded > 0 && queuedItems.length > 0 && !submitting) {
      setAnimating(true);
      let newQueuedItems = queuedItems.filter((item) => {
        return !lowerLists.includes(item.toLowerCase());
      });

      if (newQueuedItems.length) {
        let firstQueued = newQueuedItems.shift();

        animateTimeout = setTimeout(() => {
          if (unmountedRef.current) return;
          setItems([firstQueued].concat(items));
          setQueuedItems(newQueuedItems);
          setAnimating(false);
        }, 400);
      }
    }

    return () => {
      if (requestTimeout) clearTimeout(requestTimeout);
      if (animateTimeout) clearTimeout(animateTimeout);
    };
  }, [queuedItems, items, prompt, submitting, keepers, starters, context, currentScreen, ignoreItemCount, hasNonRetryableError, handleTerminalOutcome]);

  useEffect(() => {
    // our submit logic
    const itemsNeeded = 6 - items.length; // need 5 items
    const haveItems = items.length > 0;

    if (prompt && itemsNeeded > 0 && submitting && currentScreen === 1 && !queueing && !hasNonRetryableError) {
      setQueueing(true);
      const lowerLists = makeLowerLists();

      //gets items from the server and throws them in the queue
      let data = {};
      data.documentId = documentId;
      data.prompt = prompt;
      if (context.length) data.context = context;
      if (list.length) data.user_selected_list = list;
      if (starters.length) data.user_starters = starters;
      if (rejected.length) data.user_rejected = rejected;

      data.items = items.filter((item) => {
        return item.length > 0;
      });

      // or use keepers if we have a list of those
      if (keepers.length) {
        data.items = keepers.filter((item) => {
          return item.length > 0;
        });
      }

      // if this is a "start" list, we need more items to guarantee enough starting cards in the list
      data.n = haveItems ? 4 : 8;

      Auth.fetchWithIdToken("/api/ai/brainstorm", data)
        .then((result) => {
          if (unmountedRef.current) return;
          if (handleTerminalOutcome(result)) return;
          if (result.items.length > 0) {
            let newQueuedItems = update(
              queuedItems.filter((item) => {
                return item.length > 0 && !lowerLists.includes(item.toLowerCase());
              }),
              { $push: result.items },
            );

            //remove duplicates in newQueuedItems
            newQueuedItems = [...new Set(newQueuedItems)];
            const readyItems = newQueuedItems.slice(0, itemsNeeded);
            const surplusItems = newQueuedItems.slice(itemsNeeded);
            const itemsToAdd = readyItems.slice(0, readyItems.length - items.length);
            const initialItemCount = itemsToAdd.length + items.length;

            setItems([...items, ...itemsToAdd]);
            // replace queuedItems with remaining queuedItems
            setQueuedItems(queuedItems.concat(surplusItems));

            if (initialItemCount >= itemsNeeded) {
              makeStartersKeepers();
              setSubmitting(false);
              setCurrentScreen(2);
              setProTip(3);
              if (seenTips.indexOf(3) < 0) {
                setShowProTip(true);
              }
            } else {
              //trigger a new submit
              setSubmitting(false);
              setSubmitting(true);
            }
            setQueueing(false);
            setErrorMessage("");
          } else if (items.length === 0) {
            // TODO: show nice msg
            setSubmitting(false);
            console.log("no items returned");
            setErrorMessage("The AI is stumped. Try adding more examples or context.");
            sendAppEvent("Brainstorm - User saw AI error");
          }
        })
        .catch((err) => {
          if (unmountedRef.current) return;
          console.error("[BRAINSTORM DEBUG] Initial request error:", err);

          // Check if it's a 400-level error (client error) - don't retry these
          if (err.status >= 400 && err.status < 500) {
            setHasNonRetryableError(true);
            setErrorMessage("There was an issue with your request. Try changing your prompt or starting over.");
            sendAppEvent("Brainstorm - User saw client error", {
              cardId: listId,
              error: err.message || "Unknown error",
              status: err.status,
            });
          } else {
            setErrorMessage("Connection error. Please refresh in a few seconds.");
            sendAppEvent("Brainstorm - User saw connection error");
          }

          setSubmitting(false);
        });
    }
  }, [queuedItems, queueing, items, prompt, submitting, keepers, starters, context, currentScreen, hasNonRetryableError, handleTerminalOutcome]);

  useEffect(() => {
    needsSave.current = true;

    // Save whenever keepers, prompt, or prompt changes
    if (hasThumbsedUp || hasEditedStarters) {
      save.current({ starters, rejected, context, keepers, prompt, startTime, endTime, list, needsSaveRef: needsSave, documentId });
    }
  }, [keepers, prompt, starters, rejected, context, endTime]);

  useEffect(() => {
    // clear queue whenever context, or prompt changes
    // with an adequate debounce
    const clearItems = debounce(
      () => {
        setQueuedItems([]);
        setHasNonRetryableError(false); // Allow retries when user changes prompt/context
      },
      1500,
      { trailing: true },
    );

    clearItems();
  }, [context, prompt]);

  const exitOrGoBack = () => {
    if (currentScreen > 0) {
      const newScreen = currentScreen - 1;
      setCurrentScreen(newScreen);
      sendAppEvent("Brainstorm - Went Back");
    } else {
      sendAppEvent("Brainstorm - Went Back/Closed Brainstorm");
      close();
    }
  };

  const handleListChange = (listName) => {
    if (!loading) {
      if (listName !== "something-else") {
        let anExample = randomExample(listName);
        if (anExample.prompt === prompt) {
          anExample = randomExample(listName);
        }

        if (anExample) {
          setPrompt(anExample.prompt);
          setContext(anExample.context);
          setStarters(anExample.starters);
        }
      } else {
        setPrompt("");
        setContext("");
        setStarters([""]);
      }
      setList(listName);
      setCurrentScreen(1);
    }
  };

  const makeCategories = () => {
    let categories = [];
    EXAMPLES2.forEach((example) => {
      const lowered = example.category.toLowerCase().replace(/\ /g, "-");
      if (!categories.includes(lowered)) {
        categories.push(lowered);
      }
    });
    return categories;
  };

  const randomExample = (category) => {
    const categoryExamples = EXAMPLES2.filter((example) => {
      if (example.category.toLowerCase().replace(/\ /g, "") === category) {
        return true;
      }
    });

    //choose a random example
    const randomIndex = Math.floor(Math.random() * categoryExamples.length);
    const example = categoryExamples[randomIndex];
    return example;
  };

  const randomizeExample = () => {
    let newList = list;
    if (list === "something-else") {
      // a random example from any list
      newList = EXAMPLES2[Math.floor(Math.random() * EXAMPLES2.length)].category;
      newList = newList.toLowerCase();
    }

    let anExample = randomExample(newList);

    if (anExample.prompt === prompt) {
      anExample = randomExample(newList);
    }

    if (anExample) {
      setPrompt(anExample.prompt);
      setContext(anExample.context);
      setStarters(anExample.starters);
    }
  };

  const handleStartOver = () => {
    save.current.cancel();
    saverRef.current.invalidate();
    setHasThumbsedUp(false);
    setHasEditedStarters(false);
    setSaved(null);
    setItems([]);
    setKeepers([]);
    setStarters([]);
    setRejected([]);
    setQueuedItems([]);
    setPrompt("");
    setContext("");
    cardIdRef.current = false;
    setCardId(false);
    setCurrentScreen(0);
    setHasNonRetryableError(false); // Allow retries when starting over
    if (hasThumbsedUp || hasEditedStarters) {
      setStartedOver(true); //triggers the protip
      setShowProTip(true);
    }
    sendAppEvent("Brainstorm - Started Over");
  };

  const categories = makeCategories();
  const exampleGrid = categories.map((category, i) => {
    const replacedDash = category.replace(/\-/g, " ");

    return (
      <StyledGridItem
        key={category + i}
        role="button"
        tabIndex={0}
        aria-label={`Brainstorm ${replacedDash}`}
        url={category}
        onClick={() => {
          sendAppEvent("Brainstorm - Screen 1 Example Change");
          handleListChange(category);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleListChange(category);
          }
        }}
      >
        <div className="background-cover"></div>
        <div className="title">{replacedDash}</div>
      </StyledGridItem>
    );
  });

  // console.log("queued", queuedItems);

  return (
    <StyledModal
      ref={wrapperRef}
      role="dialog"
      aria-label="Brainstorm"
    >
      <SudoButton
        variant="transparent"
        aria-label="Close brainstorm"
        className="back-button !absolute top-[5vh] right-[5vw] z-[99]"
        onClick={() => {
          sendAppEvent("Brainstorm - Exited");
          close();
        }}
      >
        <SVG
          src="/assets/brainstorm/close-modal.svg"
          aria-hidden="true"
          style={{ stroke: "var(--icon-button-fill)" }}
        />
      </SudoButton>
      {currentScreen !== 2 && (
        <SudoButton
          variant="transparent"
          aria-label="Go back"
          className="!absolute top-[5vh] left-[5vw] z-[99]"
          onClick={exitOrGoBack}
        >
          <SVG
            src="/assets/brainstorm/back-modal.svg"
            aria-hidden="true"
            style={{ stroke: "var(--icon-button-fill)" }}
          />
        </SudoButton>
      )}

      {currentScreen === 2 && (
        <SudoButton
          variant="transparent"
          aria-label="Start over"
          className="!absolute top-[5vh] left-[5vw] w-[120px] z-[99]"
          style={{ color: "#b3b3b3" }}
          onClick={handleStartOver}
        >
          <SVG
            src="/assets/brainstorm/double-back.svg"
            aria-hidden="true"
          />{" "}
          <span>Start Over</span>
        </SudoButton>
      )}

      {currentScreen === 0 && (
        <StyledExamplesContainer scale={uiScale}>
          <h1>What do you want to brainstorm?</h1>
          <div className="examples">
            <StyledGrid>
              {exampleGrid}
              <StyledBlankSlate
                role="button"
                tabIndex={0}
                aria-label="Brainstorm something else"
                onClick={() => {
                  handleListChange("something-else");
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleListChange("something-else");
                  }
                }}
              >
                <div className="background-cover"></div>
                <div className="title">Something Else</div>
              </StyledBlankSlate>
            </StyledGrid>
          </div>
          {currentScreen === 0 && startedOver && showProTip && (
            <StyledStartOverProTip>
              <ProTip
                {...PROTIPS[5]}
                header="Pro Tip"
                setShowProTip={setShowProTip}
                success={() => handleSeenTip(5)}
              />
            </StyledStartOverProTip>
          )}
        </StyledExamplesContainer>
      )}

      {currentScreen === 1 && (
        <StyledBrainformContainer scale={uiScale}>
          <h1>Kickstart your Brainstorm</h1>
          <form
            ref={formRef}
            onSubmit={submit}
          >
            <Brainform
              promptPlaceholder={promptPlaceholder}
              setContext={setContext}
              setPrompt={setPrompt}
              prompt={prompt}
              context={context}
              handleListChange={handleListChange}
              list={list}
              enabled={enabled}
              submitting={submitting}
              items={items}
              setItems={setItems}
              setQueuedItems={setQueuedItems}
              userInteracted={userInteracted}
              update={update}
              proTip={proTip}
              showProTip={showProTip}
              setShowProTip={setShowProTip}
              setProTip={setProTip}
              seenTips={seenTips}
              setSeenTips={setSeenTips}
              setStarters={setStarters}
              starters={starters}
              currentScreen={currentScreen}
              setCurrentScreen={setCurrentScreen}
              submit={submit}
              setKeepers={setKeepers}
              keepers={keepers}
              randomizeExample={randomizeExample}
              setHasEditedStarters={setHasEditedStarters}
              hasEditedStarters={hasEditedStarters}
            />
          </form>
        </StyledBrainformContainer>
      )}
      {currentScreen === 2 && (
        <StyledStreamingListContainer scale={uiScale}>
          <h1>Let's brainstorm!</h1>
          <StreamingList
            setIgnoreItemCount={setIgnoreItemCount}
            items={items}
            setItems={setItems}
            userInteracted={userInteracted}
            update={update}
            prompt={prompt}
            keepers={keepers}
            setKeepers={setKeepers}
            currentScreen={currentScreen}
            setCurrentScreen={setCurrentScreen}
            closeBrainstorm={close}
            setRejected={setRejected}
            rejected={rejected}
            setQueuedItems={setQueuedItems}
            queuedItems={queuedItems}
            save={save}
            cardId={cardId}
            starters={starters}
            setStarters={setStarters}
            setEndTime={setEndTime}
            setPrompt={setPrompt}
            setContext={setContext}
            context={context}
            showProTip={showProTip}
            setShowProTip={setShowProTip}
            setProTip={setProTip}
            seenTips={seenTips}
            setSeenTips={setSeenTips}
            proTip={proTip}
            setHasThumbsedUp={setHasThumbsedUp}
          />
        </StyledStreamingListContainer>
      )}

      {currentScreen === 3 && (
        <StyledReviewContainer scale={uiScale}>
          <StyledH1>Review</StyledH1>
          <ReviewBrainstorm
            items={items}
            setItems={setItems}
            userInteracted={userInteracted}
            prompt={prompt}
            keepers={keepers}
            setKeepers={setKeepers}
            setCurrentScreen={setCurrentScreen}
            closeBrainstorm={close}
            save={save}
            cardId={cardId}
            rejected={rejected}
            setRejected={setRejected}
            startTime={startTime}
            endTime={endTime}
            saved={saved}
          />
        </StyledReviewContainer>
      )}
      {errorMessage && <StyledErrorMessage>{errorMessage}</StyledErrorMessage>}
    </StyledModal>
  );
};

export default Brainstorm;
